/**
 * 全国表示（県境・高速道路・選択中の都道府県）に描く線の仕様。CesiumJS に依存しない純関数にして、
 * 「どの線を・どの太さ・どの高さで・どの順に描くか」を単体テストで固定する（見た目の回帰防止）。
 * Cesium の Material は呼び出し側（`CesiumScene.buildNation`）が **Polyline 1 本ごとに** 作る。
 */
import type { MapLines } from '../../data/types';

/** 線の種別。色はサンプル（WebUI-Sample/3D-Portal-Google3D.html）と同じ値から変えない。 */
export type NationLineKind = 'border' | 'road' | 'selected';

/** 種別ごとの見た目。幅は Polyline の width（ピクセル）、色は CSS 色 + 不透明度。 */
export const NATION_LINE_STYLE: Readonly<
  Record<NationLineKind, { readonly color: string; readonly alpha: number; readonly width: number }>
> = {
  border: { color: '#ffffff', alpha: 0.8, width: 1.2 },
  road: { color: '#e07b39', alpha: 0.9, width: 1.6 },
  selected: { color: '#fbbf24', alpha: 1, width: 3.5 },
};

/** 1 本の線の仕様。[経度, 緯度] の列と、太さ・高さ（メートル）。 */
export interface NationLineSpec {
  kind: NationLineKind;
  /** 県境は閉じた輪郭（先頭と同じ点を末尾に足す）。高速道路は与えられた点列のまま。 */
  positions: readonly (readonly [number, number])[];
  width: number;
  height: number;
}

/** 輪郭を閉じる（先頭の点を末尾に足す）。空の輪郭はそのまま返す。 */
function closeRing(
  ring: readonly (readonly [number, number])[],
): readonly (readonly [number, number])[] {
  const first = ring[0];
  return first ? [...ring, first] : ring;
}

/**
 * 全国表示に描く線を、描画順のまま組み立てる（サンプルの buildNation と同じ順序）。
 *
 *   1. 選択中でない県の県境（細い白）
 *   2. 高速道路（オレンジ）… `roads` が false のときは出さない
 *   3. 選択中の県の県境（太い琥珀）… 最後に足すので手前に描かれる。一致する県が無ければ出さない
 *
 * @param map 県境と高速道路の点列（[経度, 緯度]）。
 * @param selected 選択中の都道府県名（`store.state.pref`）。
 * @param roads 高速道路レイヤーが有効か。
 * @param height 線の高さ（メートル）。Google 実写 3D のときは地表より高くする（`lineH()`）。
 */
export function nationLineSpecs(
  map: MapLines,
  selected: string,
  roads: boolean,
  height: number,
): NationLineSpec[] {
  const out: NationLineSpec[] = [];
  const push = (kind: NationLineKind, positions: readonly (readonly [number, number])[]): void => {
    out.push({ kind, positions, width: NATION_LINE_STYLE[kind].width, height });
  };
  if (roads) {
    for (const p of map.prefs) {
      if (p.name === selected) continue;
      for (const r of p.rings) push('border', closeRing(r));
    }
    for (const r of map.roads) push('road', r);
  }
  const sp = map.prefs.find((p) => p.name === selected);
  if (sp) for (const r of sp.rings) push('selected', closeRing(r));
  return out;
}
